import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { phoneNumberValidator, passwordValidator, valueMatchValidator } from '@unleashed/common/validators';
import { AuthorizationService } from '@unleashed/services/account';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

function adultDateOfBirthValidator(minimumAge: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const input = String(control.value);
    const birthDate = new Date(`${input}T00:00:00`);
    if (Number.isNaN(birthDate.getTime())) {
      return { invalidDateOfBirth: true };
    }

    const now = new Date();
    if (birthDate >= now) {
      return { invalidDateOfBirth: true };
    }

    const age = now.getFullYear() - birthDate.getFullYear()
      - ((now.getMonth() < birthDate.getMonth()
        || (now.getMonth() === birthDate.getMonth() && now.getDate() < birthDate.getDate())) ? 1 : 0);

    if (age < minimumAge) {
      return { underAge: true };
    }

    return null;
  };
}

type ProvisionalDraft = {
  lastName: string;
  phoneNumber: string;
  dateOfBirth: string;
};

@Component({
  selector: 'ua-provisional-account-flow',
  templateUrl: './provisional-account-flow.component.html',
  styleUrls: ['./provisional-account-flow.component.scss']
})
export class ProvisionalAccountFlowComponent implements OnInit, OnDestroy {
  @Input() accountId?: number;
  @Input() email = '';
  @Input() lastName = '';
  @Input() phoneNumber = '';
  @Output() cancelClicked = new EventEmitter<void>();

  readonly otpLength = 6;
  readonly resendCooldownSeconds = 60;

  completionForm: FormGroup;
  formDirty = false;
  otpCode = '';
  otpToken?: string;
  resendCooldown = 0;
  otpSendInProgress = false;
  otpVerifyInProgress = false;
  completionInProgress = false;
  otpError?: string;
  globalError?: string;

  private subscriptions = new Subscription();
  private cooldownInterval?: ReturnType<typeof setInterval>;

  constructor(
    private authService: AuthorizationService
  ) {
    this.completionForm = new FormGroup({
      lastName: new FormControl('', [Validators.required]),
      phoneNumber: new FormControl('', [Validators.required, phoneNumberValidator()]),
      dateOfBirth: new FormControl('', [Validators.required, adultDateOfBirthValidator(18)]),
      password: new FormControl('', [Validators.required, Validators.minLength(8), Validators.maxLength(30), passwordValidator()]),
      confirmPassword: new FormControl('', [Validators.required])
    });
    this.completionForm.validator = valueMatchValidator(this.completionForm, 'password', 'confirmPassword');
  }

  ngOnInit(): void {
    const sanitizedLastName = this.lastName.trim() === '-' ? '' : this.lastName;
    const sanitizedPhoneNumber = this.normalizePlaceholderPhone(this.phoneNumber);
    this.completionForm.patchValue({
      lastName: sanitizedLastName,
      phoneNumber: sanitizedPhoneNumber,
      dateOfBirth: ''
    });

    this.restoreDraft();
    this.subscriptions.add(
      this.completionForm.valueChanges.subscribe(() => {
        this.saveDraft();
      })
    );

    this.sendOtp();
  }

  ngOnDestroy(): void {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
      this.cooldownInterval = undefined;
    }
    this.subscriptions.unsubscribe();
  }

  onOtpCodeChange(value: string): void {
    this.otpCode = value.replace(/\D/g, '').slice(0, this.otpLength);
    this.otpError = undefined;
    this.globalError = undefined;
  }

  onOtpInput(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value ?? '';
    this.onOtpCodeChange(value);
  }

  verifyOtp(): void {
    if (!this.email || this.otpCode.length !== this.otpLength) {
      this.otpError = 'Please enter the full 6-digit code.';
      return;
    }

    this.otpVerifyInProgress = true;
    this.otpError = undefined;
    this.globalError = undefined;

    this.authService.verifyProvisionalOtp(this.email, this.otpCode)
      .pipe(take(1))
      .subscribe({
        next: response => {
          this.otpToken = response.token;
          this.otpVerifyInProgress = false;
        },
        error: err => {
          this.otpVerifyInProgress = false;
          if (err?.status === 429) {
            this.globalError = 'Too many attempts. Please request a new code.';
          } else if (err?.status === 401) {
            this.otpError = 'Code incorrect or expired.';
          } else {
            this.otpError = 'Something went wrong. Please try again.';
          }
        }
      });
  }

  resendOtp(): void {
    if (this.resendCooldown > 0 || this.otpSendInProgress) {
      return;
    }

    this.sendOtp();
  }

  completeAccount(): void {
    if (!this.accountId || !this.email || !this.otpToken) {
      this.globalError = 'Please verify your code before completing your profile.';
      return;
    }

    this.formDirty = true;
    this.completionForm.controls.lastName.markAsTouched();
    this.completionForm.controls.phoneNumber.markAsTouched();
    this.completionForm.controls.dateOfBirth.markAsTouched();
    this.completionForm.controls.password.markAsTouched();
    this.completionForm.controls.confirmPassword.markAsTouched();

    if (!this.completionForm.valid) {
      return;
    }

    this.completionInProgress = true;
    this.globalError = undefined;

    this.authService.completeProvisionalAccount(
      this.accountId,
      this.email,
      this.otpToken,
      {
        lastName: String(this.completionForm.value.lastName).trim(),
        phone: String(this.completionForm.value.phoneNumber).trim(),
        dateOfBirth: String(this.completionForm.value.dateOfBirth),
        password: String(this.completionForm.value.password)
      }
    )
      .pipe(take(1))
      .subscribe(user => {
        this.completionInProgress = false;
        if (!user.loggedIn) {
          this.authService.getError()
            .pipe(take(1))
            .subscribe(message => {
              this.globalError = message ?? 'Unable to complete your account. Please try again.';
            });
          return;
        }

        this.clearDraft();
      });
  }

  goBack(): void {
    this.cancelClicked.emit();
  }

  private sendOtp(): void {
    if (!this.email) {
      this.globalError = 'Email is required to verify your identity.';
      return;
    }

    this.otpSendInProgress = true;
    this.globalError = undefined;
    this.otpError = undefined;

    this.authService.sendProvisionalOtp(this.email)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.otpSendInProgress = false;
          this.startResendCooldown();
        },
        error: err => {
          this.otpSendInProgress = false;
          if (err?.status === 429) {
            this.globalError = 'Too many requests. Please wait before requesting a new code.';
          } else {
            this.globalError = 'Something went wrong sending your verification code. Please try again.';
          }
        }
      });
  }

  private startResendCooldown(): void {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
      this.cooldownInterval = undefined;
    }

    this.resendCooldown = this.resendCooldownSeconds;
    this.cooldownInterval = setInterval(() => {
      this.resendCooldown -= 1;
      if (this.resendCooldown <= 0) {
        this.resendCooldown = 0;
        if (this.cooldownInterval) {
          clearInterval(this.cooldownInterval);
          this.cooldownInterval = undefined;
        }
      }
    }, 1000);
  }

  private storageKey(): string {
    return `provisional-account-completion:${this.accountId ?? 'unknown'}`;
  }

  private saveDraft(): void {
    const draft: ProvisionalDraft = {
      lastName: String(this.completionForm.value.lastName ?? ''),
      phoneNumber: String(this.completionForm.value.phoneNumber ?? ''),
      dateOfBirth: String(this.completionForm.value.dateOfBirth ?? ''),
    };
    sessionStorage.setItem(this.storageKey(), JSON.stringify(draft));
  }

  private restoreDraft(): void {
    const raw = sessionStorage.getItem(this.storageKey());
    if (!raw) {
      return;
    }

    try {
      const draft = JSON.parse(raw) as ProvisionalDraft;
      this.completionForm.patchValue({
        lastName: draft.lastName ?? '',
        phoneNumber: draft.phoneNumber ?? '',
        dateOfBirth: draft.dateOfBirth ?? ''
      });
    } catch {
      this.clearDraft();
    }
  }

  private clearDraft(): void {
    sessionStorage.removeItem(this.storageKey());
  }

  private normalizePlaceholderPhone(value: string): string {
    const normalized = value?.replace(/\D/g, '') ?? '';
    if (!normalized || normalized === '0000000000') {
      return '';
    }

    return value;
  }
}
